import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  PageHeader,
  PageHeaderSkeleton,
  Stack,
  TabBar,
  type TabBarItem,
  useDelayedLoading,
} from '@leanin/ui';
import { type MembershipRequestView } from '../../../lib/queries/groups';
import {
  useApproveMembershipRequest,
  useDeclineMembershipRequest,
} from '../../../lib/mutations/groups';
import { trackIntakeRequestDecided } from '../../../lib/analytics/events';
import { useMessageMember } from '../../groups/use-message-member';
import { InlineStatus } from '../../InlineStatus';
import { AdminJoinRequestsCard, type ReviewTab } from './AdminJoinRequestsCard';
import { AdminJoinRequestsCardSkeleton } from './AdminJoinRequestsCard.skeleton';
import { ViewApplicationModal } from './ViewApplicationModal';
import {
  useLeaderRequestsByStatus,
  useReviewableJoinRequests,
} from './use-reviewable-join-requests';

// Awaiting-review / Accepted / Declined join requests for circles and networks.
// (Spaces don't have request-to-join.) The Awaiting-review tab is the leader's
// action list — approve / decline; Accepted and Declined are history, with a
// reverse-to-approve on the Declined tab (D3). Any row that carries an intake
// snapshot exposes a "View application" affordance (ViewApplicationModal).
//
// The status filter is the platform-canonical `TabBar` count-pill strip (same
// as every other filtered list surface), not a bespoke control.

type Props = {
  readonly kind: 'circle' | 'network';
  readonly groupId: string;
};

export function GroupRequestsSection({ kind, groupId }: Props) {
  const t = useTranslations('Group');
  const navigate = useNavigate();
  const [tab, setTab] = useState<ReviewTab>('pending');
  const [viewing, setViewing] = useState<MembershipRequestView | null>(null);

  // All three slices are fetched so the filter's per-status counts stay live
  // and switching tabs is instant. Each already excludes the viewer's own
  // request; the pending set also feeds the sidebar badge, so count and list
  // can't drift.
  const pending = useReviewableJoinRequests(kind, groupId);
  const accepted = useLeaderRequestsByStatus(kind, groupId, 'accepted');
  const declined = useLeaderRequestsByStatus(kind, groupId, 'declined');

  const approve = useApproveMembershipRequest();
  const decline = useDeclineMembershipRequest();
  const { messageMember, error: messageError } = useMessageMember();
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const isPending = approve.isPending || decline.isPending;

  const rows =
    tab === 'pending'
      ? pending.requests
      : tab === 'accepted'
        ? accepted.requests
        : declined.requests;
  const isLoading =
    tab === 'pending'
      ? pending.isLoading
      : tab === 'accepted'
        ? accepted.isLoading
        : declined.isLoading;

  const handleApprove = (requestId: string) => {
    setDecisionError(null);
    // A circle-applicant approval re-parents the Circle onto this Network, so
    // thread the Circle id through for the circle-network-link cache refresh.
    const row = rows.find((r) => r.id === requestId);
    const subjectCircleId =
      row?.applicantType === 'circle' ? (row.subjectCircle?.id ?? undefined) : undefined;
    // Approving a request that is currently in the Declined tab is a reversal
    // (D3 reverse-to-approve); from Awaiting review it is a fresh approval.
    const outcome = tab === 'declined' ? 'reversed' : 'approved';
    approve.mutate(
      { groupId, requestId, kind, ...(subjectCircleId ? { subjectCircleId } : {}) },
      {
        onSuccess: () => {
          if (row) {
            trackIntakeRequestDecided({
              // undefined applicantType = a legacy user-subject request.
              applicant_type: row.applicantType ?? 'user',
              destination_type: kind,
              outcome,
            });
          }
        },
        onError: (err) =>
          setDecisionError(
            err instanceof Error ? err.message : t('admin.requests.approveError'),
          ),
      },
    );
  };
  const handleDecline = (requestId: string) => {
    setDecisionError(null);
    const row = rows.find((r) => r.id === requestId);
    decline.mutate(
      { groupId, requestId, kind },
      {
        onSuccess: () => {
          if (row) {
            trackIntakeRequestDecided({
              // undefined applicantType = a legacy user-subject request.
              applicant_type: row.applicantType ?? 'user',
              destination_type: kind,
              outcome: 'declined',
            });
          }
        },
        onError: (err) =>
          setDecisionError(
            err instanceof Error ? err.message : t('admin.requests.declineError'),
          ),
      },
    );
  };

  // Entity-aware "view" navigation, shared by the request card and the review
  // modal so the destination logic lives in exactly one place.
  const viewProfile = (userId: string) =>
    navigate({ to: '/members/$id', params: { id: userId } });
  const viewCircle = (circleId: string) =>
    navigate({ to: '/circles/$id', params: { id: circleId } });

  const tabs: TabBarItem[] = [
    { id: 'pending', label: t('admin.requests.tabAwaitingReview'), count: pending.requests.length },
    { id: 'accepted', label: t('admin.requests.tabAccepted'), count: accepted.requests.length },
    { id: 'declined', label: t('admin.requests.tabDeclined'), count: declined.requests.length },
  ];

  const showSkeleton = useDelayedLoading(isLoading);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('admin.requests.title')}
        subtitle={t('admin.requests.subtitle')}
      />
      <TabBar items={tabs} value={tab} onChange={(id) => setTab(id as ReviewTab)} />

      {messageError !== null ? <InlineStatus>{messageError}</InlineStatus> : null}
      {decisionError !== null ? <InlineStatus>{decisionError}</InlineStatus> : null}

      {showSkeleton ? (
        <>
          <PageHeaderSkeleton />
          <AdminJoinRequestsCardSkeleton rows={3} />
        </>
      ) : isLoading ? null : (
        <AdminJoinRequestsCard
          requests={rows}
          status={tab}
          onApprove={handleApprove}
          onDecline={handleDecline}
          onMessage={messageMember}
          onView={setViewing}
          onViewProfile={viewProfile}
          onViewCircle={viewCircle}
          isPending={isPending}
        />
      )}

      <ViewApplicationModal
        request={viewing}
        onClose={() => setViewing(null)}
        isPending={isPending}
        onApprove={(requestId) => {
          handleApprove(requestId);
          setViewing(null);
        }}
        onDecline={(requestId) => {
          handleDecline(requestId);
          setViewing(null);
        }}
        onMessage={messageMember}
        onViewProfile={viewProfile}
        onViewCircle={viewCircle}
      />
    </Stack>
  );
}
